import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { LEADS_LIMITS, type LeadFunnelConfigPublic } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { leadsApi } from '../api/leadsApi';
import {
  addSlot,
  addVertical,
  cloneFunnelPayload,
  getFunnelConfigValidationWarnings,
  getGuardrailsNeverLines,
  getInstitutionalMetric,
  getIntakeMappings,
  getIntakeMatchWindow,
  getKeywordsLines,
  getPlaybookString,
  getRoutingOwners,
  getRoutingStrategy,
  getScoringCut,
  getScoringWeights,
  getSlotQuestion,
  getSlots,
  getVerticalCountries,
  getVerticalCountryMode,
  getVerticalFastPathSlots,
  getVerticalLabel,
  getVerticalPrimaryMagnitude,
  getVerticalThresholdMin,
  getVerticalThresholdSlot,
  getWebAnonymousAttend,
  getWebAnonymousIdentityKeys,
  isValidVerticalKeyFormat,
  listAllSlotTargets,
  listSlotKeys,
  listVerticalKeys,
  removeSlot,
  removeVertical,
  SCORING_WEIGHT_LABELS,
  setGuardrailsNeverLines,
  setInstitutionalUnconfirmed,
  setInstitutionalValue,
  setIntakeMappings,
  setIntakeMatchWindow,
  setKeywordsLines,
  setPlaybookString,
  setRoutingOwners,
  setRoutingStrategy,
  setScoringCut,
  setScoringWeight,
  setSlotEnumValues,
  setSlotQuestion,
  setVerticalCountries,
  setVerticalCountryMode,
  setVerticalFastPathSlots,
  setVerticalLabel,
  setVerticalPrimaryMagnitude,
  setVerticalThresholdMin,
  setVerticalThresholdSlot,
  setWebAnonymousAttend,
  setWebAnonymousIdentityKeys,
  SLOT_STAGE_LABELS,
  SLOT_STAGES,
  sumScoringWeights,
  updateSlotField,
  type FunnelPayload,
  type InstitutionalMetricKey,
  type IntakeMappingRow,
  type RoutingOwnerRow,
  type SlotScope,
  type SlotSnapshot,
} from '../api/funnelConfigEditor';

type SourceMode = 'template' | 'active' | 'version';

type FunnelConfigWizardProps = {
  configs: LeadFunnelConfigPublic[];
  onSaved: () => void;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className='space-y-2'>
      <Label>{label}</Label>
      {children}
      {hint ? <p className='text-xs text-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function ValidationWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <Card className='rounded-2xl border-destructive/40'>
      <CardContent className='space-y-2 pt-6'>
        <p className='text-sm font-medium text-destructive'>Revisá antes de guardar</p>
        <ul className='list-disc space-y-1 pl-5 text-sm text-destructive'>
          {warnings.map(warning => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

type SlotEditorSectionProps = {
  title: string;
  scope: SlotScope;
  payload: FunnelPayload;
  updatePayload: (updater: (draft: FunnelPayload) => void) => void;
};

function SlotEditorSection({ title, scope, payload, updatePayload }: SlotEditorSectionProps) {
  const slots = getSlots(payload, scope);

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-2'>
        <h4 className='text-sm font-medium'>{title}</h4>
        <Button
          type='button'
          size='sm'
          variant='outline'
          onClick={() => updatePayload(draft => addSlot(draft, scope))}
        >
          Agregar slot
        </Button>
      </div>
      {slots.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No hay slots en este ámbito.</p>
      ) : null}
      {slots.map(slot => (
        <SlotEditorRow
          key={`${scope}-${slot.key}`}
          scope={scope}
          slot={slot}
          updatePayload={updatePayload}
        />
      ))}
    </div>
  );
}

type SlotEditorRowProps = {
  scope: SlotScope;
  slot: SlotSnapshot;
  updatePayload: (updater: (draft: FunnelPayload) => void) => void;
};

function SlotEditorRow({ scope, slot, updatePayload }: SlotEditorRowProps) {

  return (
    <div className='space-y-3 rounded-lg border border-border p-4'>
      <div className='grid gap-3 md:grid-cols-2 lg:grid-cols-3'>
        <Field label='Clave'>
          <Input
            value={slot.key}
            onChange={event =>
              updatePayload(draft =>
                updateSlotField(draft, scope, slot.key, 'key', event.target.value),
              )
            }
          />
        </Field>
        <Field label='Tipo'>
          <select
            className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            value={slot.type}
            onChange={event =>
              updatePayload(draft =>
                updateSlotField(draft, scope, slot.key, 'type', event.target.value),
              )
            }
          >
            <option value='text'>Texto</option>
            <option value='number'>Número</option>
            <option value='enum'>Enum</option>
          </select>
        </Field>
        <Field label='Prioridad'>
          <Input
            type='number'
            min={0}
            max={999}
            value={slot.priority}
            onChange={event =>
              updatePayload(draft =>
                updateSlotField(draft, scope, slot.key, 'priority', Number(event.target.value)),
              )
            }
          />
        </Field>
        <Field label='Etapa' hint='Sólo los imprescindibles se preguntan y frenan la derivación.'>
          <select
            aria-label={`Etapa de ${slot.key}`}
            className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            value={slot.stage}
            onChange={event =>
              updatePayload(draft =>
                updateSlotField(draft, scope, slot.key, 'stage', event.target.value),
              )
            }
          >
            {SLOT_STAGES.map(stage => (
              <option key={stage} value={stage}>{SLOT_STAGE_LABELS[stage]}</option>
            ))}
          </select>
        </Field>
        {slot.type === 'number' ? (
          <>
            <Field label='Unidad'>
              <Input
                value={slot.unit ?? ''}
                onChange={event =>
                  updatePayload(draft =>
                    updateSlotField(draft, scope, slot.key, 'unit', event.target.value),
                  )
                }
              />
            </Field>
            <div className='flex items-center gap-2 pt-6'>
              <input
                id={`${scope}-${slot.key}-range`}
                type='checkbox'
                checked={slot.acceptsRange === true}
                onChange={event =>
                  updatePayload(draft =>
                    updateSlotField(draft, scope, slot.key, 'acceptsRange', event.target.checked),
                  )
                }
              />
              <Label htmlFor={`${scope}-${slot.key}-range`}>Acepta rango</Label>
            </div>
          </>
        ) : null}
      </div>
      {slot.type === 'enum' ? (
        <Field label='Valores del enum' hint='Uno por línea.'>
          <Textarea
            value={slot.values?.join('\n') ?? ''}
            onChange={event =>
              updatePayload(draft => {
                const lines = event.target.value
                  .split('\n')
                  .map(line => line.trim())
                  .filter(Boolean);
                setSlotEnumValues(draft, scope, slot.key, lines);
              })
            }
            rows={3}
          />
        </Field>
      ) : null}
      <Field label='Pregunta (opcional)'>
        <Input
          value={slot.question ?? ''}
          onChange={event =>
            updatePayload(draft =>
              updateSlotField(draft, scope, slot.key, 'question', event.target.value),
            )
          }
        />
      </Field>
      <Button
        type='button'
        size='sm'
        variant='outline'
        onClick={() => updatePayload(draft => removeSlot(draft, scope, slot.key))}
      >
        Quitar slot
      </Button>
    </div>
  );
}

type VerticalStructureCardProps = {
  verticalKey: string;
  payload: FunnelPayload;
  commonSlotKeys: string[];
  updatePayload: (updater: (draft: FunnelPayload) => void) => void;
};

function VerticalStructureCard({
  verticalKey,
  payload,
  commonSlotKeys,
  updatePayload,
}: VerticalStructureCardProps) {
  const verticalSlotKeys = listSlotKeys(payload, verticalKey);
  const fastPathOptions = [...new Set([...verticalSlotKeys, ...commonSlotKeys])];
  const selectedFastPath = new Set(getVerticalFastPathSlots(payload, verticalKey));

  const toggleFastPath = (slotKey: string) => {
    updatePayload(draft => {
      const current = getVerticalFastPathSlots(draft, verticalKey);
      const next = current.includes(slotKey)
        ? current.filter(key => key !== slotKey)
        : [...current, slotKey];
      setVerticalFastPathSlots(draft, verticalKey, next);
    });
  };

  return (
    <Card className='rounded-2xl'>
      <CardHeader className='flex flex-row items-center justify-between pb-2'>
        <CardTitle className='text-base'>{getVerticalLabel(payload, verticalKey)}</CardTitle>
        <Button
          type='button'
          size='sm'
          variant='outline'
          onClick={() => updatePayload(draft => removeVertical(draft, verticalKey))}
        >
          Quitar vertical
        </Button>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='grid gap-4 md:grid-cols-2'>
          <Field label='Magnitud principal'>
            <select
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={getVerticalPrimaryMagnitude(payload, verticalKey)}
              onChange={event =>
                updatePayload(draft =>
                  setVerticalPrimaryMagnitude(draft, verticalKey, event.target.value),
                )
              }
            >
              <option value=''>Seleccionar slot</option>
              {verticalSlotKeys.map(key => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </Field>
          <Field label='Slot de umbral'>
            <select
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={getVerticalThresholdSlot(payload, verticalKey)}
              onChange={event =>
                updatePayload(draft =>
                  setVerticalThresholdSlot(draft, verticalKey, event.target.value),
                )
              }
            >
              <option value=''>Seleccionar slot</option>
              {verticalSlotKeys.map(key => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </Field>
          <Field label='Mínimo para derivar' hint='Debajo del mínimo el lead queda en revisión y se avisa al comercial. Vacío = sin mínimo.'>
            <Input
              type='number'
              value={getVerticalThresholdMin(payload, verticalKey) ?? ''}
              onChange={event => {
                const raw = event.target.value.trim();
                updatePayload(draft =>
                  setVerticalThresholdMin(draft, verticalKey, raw === '' ? null : Number(raw)),
                );
              }}
            />
          </Field>
        </div>
        <Field label='Fast path' hint='Slots que se piden antes en el flujo rápido (vertical o comunes).'>
          <div className='flex flex-wrap gap-3'>
            {fastPathOptions.map(slotKey => (
              <label key={slotKey} className='flex items-center gap-2 text-sm'>
                <input
                  type='checkbox'
                  checked={selectedFastPath.has(slotKey)}
                  onChange={() => toggleFastPath(slotKey)}
                />
                {slotKey}
              </label>
            ))}
          </div>
        </Field>
        <SlotEditorSection
          title='Slots de la vertical'
          scope={verticalKey}
          payload={payload}
          updatePayload={updatePayload}
        />
      </CardContent>
    </Card>
  );
}

type RoutingOwnersEditorProps = {
  payload: FunnelPayload;
  updatePayload: (updater: (draft: FunnelPayload) => void) => void;
};

function RoutingOwnersEditor({ payload, updatePayload }: RoutingOwnersEditorProps) {
  const rows = getRoutingOwners(payload);

  const updateRows = (next: RoutingOwnerRow[]) => {
    updatePayload(draft => setRoutingOwners(draft, next));
  };

  return (
    <div className='space-y-3'>
      {rows.map((row, index) => (
        <div key={`owner-${index}`} className='grid gap-2 rounded-lg border border-border p-3 md:grid-cols-[1fr_1fr_1fr_1fr_auto]'>
          <Input
            type='number'
            placeholder='userId'
            value={row.userId}
            onChange={event => {
              const next = [...rows];
              next[index] = { ...row, userId: Number(event.target.value) };
              updateRows(next);
            }}
          />
          <Input
            placeholder='Verticales (coma)'
            value={row.verticals.join(', ')}
            onChange={event => {
              const next = [...rows];
              next[index] = {
                ...row,
                verticals: event.target.value.split(',').map(v => v.trim()).filter(Boolean),
              };
              updateRows(next);
            }}
          />
          <Input
            placeholder='Países (coma)'
            value={row.countries.join(', ')}
            onChange={event => {
              const next = [...rows];
              next[index] = {
                ...row,
                countries: event.target.value.split(',').map(v => v.trim()).filter(Boolean),
              };
              updateRows(next);
            }}
          />
          <Input
            type='number'
            placeholder='Peso (opc.)'
            value={row.weight ?? ''}
            onChange={event => {
              const next = [...rows];
              const raw = event.target.value.trim();
              next[index] = {
                ...row,
                weight: raw === '' ? undefined : Number(raw),
              };
              updateRows(next);
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => updateRows(rows.filter((_, rowIndex) => rowIndex !== index))}
          >
            Quitar
          </Button>
        </div>
      ))}
      <Button
        type='button'
        size='sm'
        variant='outline'
        onClick={() => updateRows([...rows, { userId: 0, verticals: [], countries: [] }])}
      >
        Agregar owner
      </Button>
    </div>
  );
}

const INSTITUTIONAL_FIELDS: { key: InstitutionalMetricKey; label: string }[] = [
  { key: 'yearsExperience', label: 'Años de experiencia' },
  { key: 'projects', label: 'Proyectos' },
  { key: 'countries', label: 'Países' },
];

export function FunnelConfigWizard({ configs, onSaved, onError, onMessage }: FunnelConfigWizardProps) {
  const [sourceMode, setSourceMode] = useState<SourceMode>('template');
  const [sourceConfigId, setSourceConfigId] = useState('');
  const [payload, setPayload] = useState<FunnelPayload | null>(null);
  const [note, setNote] = useState('');
  const [loadingSource, setLoadingSource] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [questionScope, setQuestionScope] = useState<string>('common');
  const [intakeSource, setIntakeSource] = useState<'META_FORM' | 'WEB_FORM'>('META_FORM');
  const [newVerticalKey, setNewVerticalKey] = useState('');
  const [newVerticalLabel, setNewVerticalLabel] = useState('');
  const [newVerticalError, setNewVerticalError] = useState('');

  const activeConfig = configs.find(row => row.status === 'ACTIVE');
  const configsRef = useRef(configs);
  configsRef.current = configs;

  const applyLoadedPayload = useCallback(
    (nextPayload: FunnelPayload, message: string) => {
      setPayload(cloneFunnelPayload(nextPayload));
      const firstVertical = listVerticalKeys(nextPayload)[0];
      setQuestionScope(firstVertical ?? 'common');
      onMessage(message);
    },
    [onMessage],
  );

  const loadActivePayload = useCallback(async (): Promise<FunnelPayload | null> => {
    const activeId = configsRef.current.find(row => row.status === 'ACTIVE')?.id;
    if (!activeId) {
      return null;
    }
    const detail = await leadsApi.getFunnelConfig(activeId);
    if (detail.payload.schemaVersion !== 2) {
      throw new Error(
        'La versión activa usa un formato anterior (v1). Cargá la plantilla de referencia v2 y aplicá una versión nueva.',
      );
    }
    return detail.payload;
  }, []);

  const loadSource = useCallback(async () => {
    setLoadingSource(true);
    onError('');
    try {
      let nextPayload: FunnelPayload;
      if (sourceMode === 'template') {
        try {
          nextPayload = await leadsApi.getReferenceTemplate();
        } catch (templateErr) {
          const fromActive = await loadActivePayload();
          if (!fromActive) {
            throw templateErr;
          }
          setSourceMode('active');
          applyLoadedPayload(
            fromActive,
            'Plantilla del servidor no disponible; se cargó la versión activa.',
          );
          return;
        }
      } else if (sourceMode === 'active') {
        const fromActive = await loadActivePayload();
        if (!fromActive) {
          throw new Error('No hay configuración activa para clonar.');
        }
        nextPayload = fromActive;
      } else {
        if (!sourceConfigId) {
          throw new Error('Elegí una versión para clonar.');
        }
        const detail = await leadsApi.getFunnelConfig(sourceConfigId);
        nextPayload = detail.payload;
      }
      applyLoadedPayload(nextPayload, 'Base cargada. Ajustá los parámetros y guardá un borrador.');
    } catch (err) {
      onError(contactAgentError(err));
    } finally {
      setLoadingSource(false);
    }
  }, [applyLoadedPayload, loadActivePayload, onError, sourceConfigId, sourceMode]);

  useEffect(() => {
    if (sourceMode === 'version' && !sourceConfigId) {
      return;
    }
    void loadSource();
  }, [sourceMode, sourceConfigId, loadSource]);

  useEffect(() => {
    if (payload || loadingSource || sourceMode !== 'template') {
      return;
    }
    const activeId = configs.find(row => row.status === 'ACTIVE')?.id;
    if (!activeId) {
      return;
    }
    setSourceMode('active');
  }, [configs, loadingSource, payload, sourceMode]);

  const updatePayload = useCallback((updater: (draft: FunnelPayload) => void) => {
    setPayload(current => {
      if (!current) {
        return current;
      }
      const next = cloneFunnelPayload(current);
      updater(next);
      return next;
    });
  }, []);

  const verticalKeys = useMemo(() => (payload ? listVerticalKeys(payload) : []), [payload]);
  const weightSum = payload ? sumScoringWeights(payload) : 0;
  const slotTargets = payload ? listAllSlotTargets(payload) : [];
  const intakeRows = payload ? getIntakeMappings(payload, intakeSource) : [];
  const commonSlotKeys = useMemo(
    () => (payload ? listSlotKeys(payload, 'common') : []),
    [payload],
  );
  const validationWarnings = useMemo(
    () => (payload ? getFunnelConfigValidationWarnings(payload) : []),
    [payload],
  );

  const validatePayloadSize = (): boolean => {
    if (!payload) {
      onError('Cargá una base antes de guardar.');
      return false;
    }
    const size = JSON.stringify(payload).length;
    if (size > LEADS_LIMITS.configPayloadMaxBytes) {
      onError('La configuración supera el tamaño máximo permitido (512 KB).');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!payload || !validatePayloadSize()) {
      return;
    }
    setSaving(true);
    onError('');
    try {
      const created = await leadsApi.importFunnelConfig(payload, note.trim() || undefined);
      onMessage(`Borrador v${created.version} guardado. Revisá y activá desde el historial.`);
      setNote('');
      onSaved();
    } catch (err) {
      onError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    if (!payload || !validatePayloadSize()) {
      return;
    }
    setApplying(true);
    onError('');
    try {
      const applied = await leadsApi.applyFunnelConfig(payload, note.trim() || undefined);
      onMessage(`Versión v${applied.version} aplicada. Los próximos mensajes ya usan esta configuración.`);
      setNote('');
      onSaved();
    } catch (err) {
      onError(contactAgentError(err));
    } finally {
      setApplying(false);
    }
  };

  const updateIntakeRows = (rows: IntakeMappingRow[]) => {
    updatePayload(draft => setIntakeMappings(draft, intakeSource, rows));
  };

  if (!payload) {
    return (
      <Card className='rounded-2xl'>
        <CardContent className='py-10 text-center text-sm text-muted-foreground'>
          {loadingSource ? 'Cargando plantilla…' : 'No se pudo cargar la configuración base.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-4'>
      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Punto de partida</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              size='sm'
              variant={sourceMode === 'template' ? 'default' : 'outline'}
              onClick={() => setSourceMode('template')}
            >
              Plantilla Setup Informática
            </Button>
            <Button
              type='button'
              size='sm'
              variant={sourceMode === 'active' ? 'default' : 'outline'}
              disabled={!activeConfig}
              onClick={() => setSourceMode('active')}
            >
              Clonar activa {activeConfig ? `(v${activeConfig.version})` : ''}
            </Button>
            <Button
              type='button'
              size='sm'
              variant={sourceMode === 'version' ? 'default' : 'outline'}
              onClick={() => setSourceMode('version')}
            >
              Clonar otra versión
            </Button>
          </div>

          {sourceMode === 'version' ? (
            <select
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={sourceConfigId}
              onChange={event => setSourceConfigId(event.target.value)}
            >
              <option value=''>Seleccionar versión</option>
              {configs.map(config => (
                <option key={config.id} value={config.id}>
                  v{config.version} · {config.status}
                  {config.note ? ` · ${config.note}` : ''}
                </option>
              ))}
            </select>
          ) : null}

          <Button type='button' variant='outline' size='sm' disabled={loadingSource} onClick={() => void loadSource()}>
            {loadingSource ? 'Cargando…' : 'Recargar base'}
          </Button>
        </CardContent>
      </Card>

      <ValidationWarnings warnings={validationWarnings} />

      <Tabs defaultValue='conversation'>
        <TabsList className='flex h-auto flex-wrap'>
          <TabsTrigger value='conversation'>Conversación</TabsTrigger>
          <TabsTrigger value='scoring'>Calificación</TabsTrigger>
          <TabsTrigger value='structure'>Estructura</TabsTrigger>
          <TabsTrigger value='verticals'>Verticales</TabsTrigger>
          <TabsTrigger value='questions'>Preguntas</TabsTrigger>
          <TabsTrigger value='intake'>Formularios</TabsTrigger>
          <TabsTrigger value='web-anonymous'>Web anónima</TabsTrigger>
          <TabsTrigger value='institutional'>Institucional</TabsTrigger>
          <TabsTrigger value='routing'>Ruteo</TabsTrigger>
        </TabsList>

        <TabsContent value='conversation' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <Field
                label='Pregunta de clasificación'
                hint='La primera pregunta que orienta al prospecto hacia el producto correcto.'
              >
                <Textarea
                  value={typeof payload.classificationQuestion === 'string' ? payload.classificationQuestion : ''}
                  onChange={event =>
                    updatePayload(draft => {
                      draft.classificationQuestion = event.target.value.slice(0, 500);
                    })
                  }
                  rows={3}
                />
              </Field>
              <Field label='Tono del bot' hint='Instrucciones de estilo para el agente.'>
                <Textarea
                  value={getPlaybookString(payload, ['tone'])}
                  onChange={event => updatePayload(draft => setPlaybookString(draft, ['tone'], event.target.value))}
                  rows={2}
                />
              </Field>
              <Field label='Respuesta sobre precios' hint='Qué debe decir el bot si preguntan por precio.'>
                <Textarea
                  value={getPlaybookString(payload, ['guardrails', 'price'])}
                  onChange={event =>
                    updatePayload(draft => setPlaybookString(draft, ['guardrails', 'price'], event.target.value))
                  }
                  rows={2}
                />
              </Field>
              <Field label='Cierre fuera de mercado'>
                <Textarea
                  value={getPlaybookString(payload, ['closings', 'outOfMarket'])}
                  onChange={event =>
                    updatePayload(draft => setPlaybookString(draft, ['closings', 'outOfMarket'], event.target.value))
                  }
                  rows={3}
                />
              </Field>
              <Field label='Cierre por país bloqueado'>
                <Textarea
                  value={getPlaybookString(payload, ['closings', 'countryBlocked'])}
                  onChange={event =>
                    updatePayload(draft => setPlaybookString(draft, ['closings', 'countryBlocked'], event.target.value))
                  }
                  rows={3}
                />
              </Field>
              <Field
                label='Nunca prometer (guardrails)'
                hint='Temas que el bot no debe comprometer. Una por línea.'
              >
                <Textarea
                  value={getGuardrailsNeverLines(payload)}
                  onChange={event =>
                    updatePayload(draft => setGuardrailsNeverLines(draft, event.target.value))
                  }
                  rows={4}
                />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='scoring' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <Field
                label={`Umbral para calificar (${getScoringCut(payload)})`}
                hint='Leads con score igual o superior se consideran calificados; por debajo van a revisión.'
              >
                <input
                  type='range'
                  min={0}
                  max={100}
                  value={getScoringCut(payload)}
                  onChange={event => updatePayload(draft => setScoringCut(draft, Number(event.target.value)))}
                  className='w-full'
                />
              </Field>
              <div className='flex items-center gap-2 text-sm'>
                <span className='text-muted-foreground'>Suma de pesos:</span>
                <Badge variant={weightSum === 100 ? 'default' : 'secondary'}>{weightSum}</Badge>
                {weightSum !== 100 ? (
                  <span className='text-xs text-muted-foreground'>No tiene que sumar 100, pero ayuda como referencia.</span>
                ) : null}
              </div>
              <div className='grid gap-4 md:grid-cols-2'>
                {Object.entries(getScoringWeights(payload)).map(([key, value]) => (
                  <Field key={key} label={SCORING_WEIGHT_LABELS[key] ?? key}>
                    <Input
                      type='number'
                      min={0}
                      max={100}
                      value={value}
                      onChange={event =>
                        updatePayload(draft => setScoringWeight(draft, key, Number(event.target.value)))
                      }
                    />
                  </Field>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='structure' className='space-y-4'>
          {verticalKeys.map(verticalKey => (
            <VerticalStructureCard
              key={verticalKey}
              verticalKey={verticalKey}
              payload={payload}
              commonSlotKeys={commonSlotKeys}
              updatePayload={updatePayload}
            />
          ))}
          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle className='text-base'>Agregar vertical</CardTitle>
            </CardHeader>
            <CardContent className='grid gap-4 md:grid-cols-2'>
              <Field label='Clave interna' hint='Solo minúsculas, números y guión bajo (ej: transporte).'>
                <Input
                  value={newVerticalKey}
                  onChange={event => {
                    setNewVerticalKey(event.target.value.slice(0, 64));
                    setNewVerticalError('');
                  }}
                  placeholder='mi_vertical'
                />
              </Field>
              <Field label='Nombre visible'>
                <Input
                  value={newVerticalLabel}
                  onChange={event => setNewVerticalLabel(event.target.value.slice(0, 120))}
                  placeholder='Mi producto'
                />
              </Field>
              {newVerticalError ? (
                <p className='text-sm text-destructive md:col-span-2'>{newVerticalError}</p>
              ) : null}
              <Button
                type='button'
                variant='outline'
                className='md:col-span-2 md:w-fit'
                onClick={() => {
                  const key = newVerticalKey.trim().toLowerCase();
                  if (!isValidVerticalKeyFormat(key)) {
                    setNewVerticalError('La clave debe usar solo a-z, 0-9 y _.');
                    return;
                  }
                  if (verticalKeys.includes(key)) {
                    setNewVerticalError('Ya existe una vertical con esa clave.');
                    return;
                  }
                  updatePayload(draft => {
                    addVertical(draft, key, newVerticalLabel.trim() || key);
                  });
                  setNewVerticalKey('');
                  setNewVerticalLabel('');
                  setNewVerticalError('');
                }}
              >
                Agregar vertical
              </Button>
            </CardContent>
          </Card>
          <Card className='rounded-2xl'>
            <CardHeader>
              <CardTitle className='text-base'>Slots comunes</CardTitle>
            </CardHeader>
            <CardContent>
              <SlotEditorSection
                title='Datos compartidos por todos los verticales'
                scope='common'
                payload={payload}
                updatePayload={updatePayload}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='verticals' className='space-y-4'>
          {verticalKeys.map(verticalKey => (
            <Card key={verticalKey} className='rounded-2xl'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base'>{getVerticalLabel(payload, verticalKey)}</CardTitle>
              </CardHeader>
              <CardContent className='grid gap-4 md:grid-cols-2'>
                <Field label='Nombre visible'>
                  <Input
                    value={getVerticalLabel(payload, verticalKey)}
                    onChange={event =>
                      updatePayload(draft => setVerticalLabel(draft, verticalKey, event.target.value))
                    }
                  />
                </Field>
                <Field label='Umbral mínimo de magnitud' hint='Vacío = sin mínimo numérico.'>
                  <Input
                    type='number'
                    value={getVerticalThresholdMin(payload, verticalKey) ?? ''}
                    onChange={event => {
                      const raw = event.target.value.trim();
                      updatePayload(draft =>
                        setVerticalThresholdMin(
                          draft,
                          verticalKey,
                          raw === '' ? null : Number(raw),
                        ),
                      );
                    }}
                  />
                </Field>
                <Field label='Países permitidos' hint='Códigos ISO separados por coma (AR, CL, EC).'>
                  <Input
                    value={getVerticalCountries(payload, verticalKey)}
                    onChange={event =>
                      updatePayload(draft => setVerticalCountries(draft, verticalKey, event.target.value))
                    }
                  />
                </Field>
                <Field label='Si el país no está en la lista'>
                  <select
                    className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                    value={getVerticalCountryMode(payload, verticalKey)}
                    onChange={event =>
                      updatePayload(draft =>
                        setVerticalCountryMode(draft, verticalKey, event.target.value as 'CUT' | 'WARN'),
                      )
                    }
                  >
                    <option value='WARN'>Advertir y seguir</option>
                    <option value='CUT'>Cerrar (no calificado)</option>
                  </select>
                </Field>
                <Field label='Pistas de la vertical' hint='Una por línea. Ayudan a reconocer a qué producto apunta el prospecto; no puntúan.'>
                  <Textarea
                    value={getKeywordsLines(payload, ['verticals', verticalKey, 'painKeywords'])}
                    onChange={event =>
                      updatePayload(draft =>
                        setKeywordsLines(draft, ['verticals', verticalKey, 'painKeywords'], event.target.value),
                      )
                    }
                    rows={4}
                  />
                </Field>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value='questions' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <Field label='Ámbito de preguntas'>
                <select
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  value={questionScope}
                  onChange={event => setQuestionScope(event.target.value)}
                >
                  <option value='common'>Datos comunes (todos los verticales)</option>
                  {verticalKeys.map(key => (
                    <option key={key} value={key}>
                      {getVerticalLabel(payload, key)}
                    </option>
                  ))}
                </select>
              </Field>
              {listSlotKeys(payload, questionScope).map(slotKey => (
                <Field key={slotKey} label={slotKey}>
                  <Textarea
                    value={getSlotQuestion(payload, questionScope, slotKey)}
                    onChange={event =>
                      updatePayload(draft => setSlotQuestion(draft, questionScope, slotKey, event.target.value))
                    }
                    rows={2}
                  />
                </Field>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='intake' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <Field label='Ventana de emparejamiento (días)' hint='Cuántos días puede pasar entre el formulario y el primer WhatsApp.'>
                <Input
                  type='number'
                  min={1}
                  max={90}
                  value={getIntakeMatchWindow(payload)}
                  onChange={event =>
                    updatePayload(draft => setIntakeMatchWindow(draft, Number(event.target.value)))
                  }
                />
              </Field>
              <div className='flex gap-2'>
                <Button
                  type='button'
                  size='sm'
                  variant={intakeSource === 'META_FORM' ? 'default' : 'outline'}
                  onClick={() => setIntakeSource('META_FORM')}
                >
                  Meta Lead Ads
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant={intakeSource === 'WEB_FORM' ? 'default' : 'outline'}
                  onClick={() => setIntakeSource('WEB_FORM')}
                >
                  Formulario web
                </Button>
              </div>
              <div className='space-y-3'>
                {intakeRows.map((row, index) => (
                  <div key={`${row.formField}-${index}`} className='grid gap-2 md:grid-cols-[1fr_1fr_auto]'>
                    <Input
                      placeholder='Campo del formulario'
                      value={row.formField}
                      onChange={event => {
                        const next = [...intakeRows];
                        next[index] = { ...row, formField: event.target.value };
                        updateIntakeRows(next);
                      }}
                    />
                    <select
                      className='rounded-md border border-input bg-background px-3 py-2 text-sm'
                      value={row.target}
                      onChange={event => {
                        const next = [...intakeRows];
                        next[index] = { ...row, target: event.target.value };
                        updateIntakeRows(next);
                      }}
                    >
                      <option value=''>Destino</option>
                      {slotTargets.map(target => (
                        <option key={target} value={target}>
                          {target}
                        </option>
                      ))}
                    </select>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => updateIntakeRows(intakeRows.filter((_, rowIndex) => rowIndex !== index))}
                    >
                      Quitar
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => updateIntakeRows([...intakeRows, { formField: '', target: '' }])}
              >
                Agregar mapeo
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='web-anonymous' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <div className='flex items-center gap-2'>
                <input
                  id='web-anonymous-attend'
                  type='checkbox'
                  checked={getWebAnonymousAttend(payload)}
                  onChange={event =>
                    updatePayload(draft => setWebAnonymousAttend(draft, event.target.checked))
                  }
                />
                <Label htmlFor='web-anonymous-attend'>Atender visitantes web sin identidad previa</Label>
              </div>
              <Field
                label='Slots de identidad (orden)'
                hint='Deben existir en slots comunes. El orden define en qué secuencia se piden.'
              >
                <div className='space-y-2'>
                  {commonSlotKeys.map(slotKey => {
                    const ordered = getWebAnonymousIdentityKeys(payload);
                    const checked = ordered.includes(slotKey);
                    const index = ordered.indexOf(slotKey);
                    return (
                      <div key={slotKey} className='flex flex-wrap items-center gap-2 rounded-md border border-border p-2'>
                        <input
                          type='checkbox'
                          checked={checked}
                          onChange={event => {
                            updatePayload(draft => {
                              const current = getWebAnonymousIdentityKeys(draft);
                              const next = event.target.checked
                                ? [...current, slotKey]
                                : current.filter(key => key !== slotKey);
                              setWebAnonymousIdentityKeys(draft, next);
                            });
                          }}
                        />
                        <span className='text-sm'>{slotKey}</span>
                        {checked ? (
                          <>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              disabled={index <= 0}
                              onClick={() =>
                                updatePayload(draft => {
                                  const current = [...getWebAnonymousIdentityKeys(draft)];
                                  if (index <= 0) {
                                    return;
                                  }
                                  [current[index - 1], current[index]] = [current[index], current[index - 1]];
                                  setWebAnonymousIdentityKeys(draft, current);
                                })
                              }
                            >
                              ↑
                            </Button>
                            <Button
                              type='button'
                              size='sm'
                              variant='outline'
                              disabled={index < 0 || index >= ordered.length - 1}
                              onClick={() =>
                                updatePayload(draft => {
                                  const current = [...getWebAnonymousIdentityKeys(draft)];
                                  if (index < 0 || index >= current.length - 1) {
                                    return;
                                  }
                                  [current[index + 1], current[index]] = [current[index], current[index + 1]];
                                  setWebAnonymousIdentityKeys(draft, current);
                                })
                              }
                            >
                              ↓
                            </Button>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Field>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='institutional' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-6 pt-6'>
              {INSTITUTIONAL_FIELDS.map(field => {
                const metric = getInstitutionalMetric(payload, field.key);
                return (
                  <div key={field.key} className='grid gap-4 md:grid-cols-2'>
                    <Field label={field.label}>
                      <Input
                        type='number'
                        value={metric.value}
                        onChange={event =>
                          updatePayload(draft =>
                            setInstitutionalValue(draft, field.key, Number(event.target.value)),
                          )
                        }
                      />
                    </Field>
                    <div className='flex items-center gap-2 pt-6'>
                      <input
                        id={`inst-${field.key}-unconfirmed`}
                        type='checkbox'
                        checked={metric.unconfirmed}
                        onChange={event =>
                          updatePayload(draft =>
                            setInstitutionalUnconfirmed(draft, field.key, event.target.checked),
                          )
                        }
                      />
                      <Label htmlFor={`inst-${field.key}-unconfirmed`}>Sin confirmar con el prospecto</Label>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='routing' className='space-y-4'>
          <Card className='rounded-2xl'>
            <CardContent className='space-y-4 pt-6'>
              <Field label='Estrategia de asignación'>
                <select
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  value={getRoutingStrategy(payload)}
                  onChange={event =>
                    updatePayload(draft => setRoutingStrategy(draft, event.target.value))
                  }
                >
                  <option value='ROUND_ROBIN'>ROUND_ROBIN</option>
                  <option value='BY_VERTICAL'>BY_VERTICAL</option>
                  <option value='BY_COUNTRY'>BY_COUNTRY</option>
                </select>
              </Field>
              <Field label='Owners' hint='userId numérico; verticals y países opcionales separados por coma.'>
                <RoutingOwnersEditor payload={payload} updatePayload={updatePayload} />
              </Field>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Guardar cambios</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Field label='Nota de esta versión (opcional)'>
            <Input
              value={note}
              onChange={event => setNote(event.target.value)}
              placeholder='Ej: ajuste umbral flotas y preguntas WMS'
              maxLength={500}
            />
          </Field>
          <div className='flex flex-wrap gap-2 text-sm text-muted-foreground'>
            <Badge variant='outline'>{verticalKeys.length} verticales</Badge>
            <Badge variant='outline'>Cut {getScoringCut(payload)}</Badge>
            <Badge variant='outline'>Intake {getIntakeMatchWindow(payload)} días</Badge>
          </div>
          <div className='flex flex-wrap gap-2'>
            <Button type='button' disabled={applying || saving} onClick={() => void handleApply()}>
              {applying ? 'Aplicando…' : 'Guardar y aplicar ahora'}
            </Button>
            <Button type='button' variant='outline' disabled={saving || applying} onClick={() => void handleSave()}>
              {saving ? 'Guardando…' : 'Guardar como borrador'}
            </Button>
          </div>
          <p className='text-xs text-muted-foreground'>
            "Aplicar ahora" publica una versión nueva y el bot la usa con los leads nuevos. Las
            conversaciones ya abiertas siguen con la versión con la que empezaron. El historial
            conserva las versiones anteriores para volver atrás.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
